import { ChangeDetectorRef, DestroyRef, inject, Pipe, PipeTransform } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateService } from '@ngx-translate/core';

import { APP_DATE_MONTH_KEYS, formatAppDate } from './app-date.utils';

/**
 * Formats a `Date` or ISO string into the TotalEnergies corporate display format.
 *
 * Usage (date-only, default):  `{{ value | appDate }}`
 * Usage (date + time):         `{{ value | appDate: 'datetime' }}`
 *
 * Null / undefined / empty → `—`.
 *
 * Month abbreviations are resolved from `TranslateService` at render time, so
 * the output follows the active UI language (EN/FR toggle in the header).
 *
 * The pipe is declared `pure: false` because its result depends on the current
 * translation language, which is not captured in the pipe's arguments.
 *
 * OnPush reflow: the pipe injects `ChangeDetectorRef` optionally (it is a
 * view-level token and is unavailable outside a component context, e.g. in
 * direct test instantiation).  When present, the pipe subscribes to
 * `TranslateService.onLangChange` and calls `cdr.markForCheck()` so that an
 * OnPush host component is marked dirty and re-evaluated on language switch.
 * This makes the reflow unconditional — it does not rely on `TranslatePipe`
 * being co-located in the same template.
 *
 * Performance: the 12-entry month array is cached and only rebuilt when
 * `TranslateService.currentLang` changes, so repeated calls within the same
 * render cycle (e.g. inside a row loop) pay a single array allocation.
 *
 * Cache poisoning guard: `instant()` returns the key string itself when
 * translations are not yet loaded.  The cache is only written when all
 * resolved values differ from their keys; otherwise the values are used for
 * the current call only and the next call retries.
 */
@Pipe({
  name: 'appDate',
  standalone: true,
  pure: false,
})
export class AppDatePipe implements PipeTransform {
  private readonly _translate = inject(TranslateService);

  /** Sentinel — empty string is never a valid BCP 47 language tag. */
  private _cachedLang = '';
  private _cachedMonths: string[] = [];

  constructor() {
    // ChangeDetectorRef is a view-level token: available when the pipe is
    // instantiated inside a component template, but null when instantiated
    // directly in tests via runInInjectionContext().
    const cdr = inject(ChangeDetectorRef, { optional: true });
    if (cdr) {
      // Mark the host OnPush component dirty whenever the language changes so
      // Angular includes it in the next CD cycle and re-evaluates this pipe.
      // The subscription is torn down via DestroyRef so it never outlives the
      // component that owns this pipe instance.
      const destroyRef = inject(DestroyRef);
      this._translate.onLangChange.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => cdr.markForCheck());
    }
  }

  transform(value: Date | string | null | undefined, mode: 'date' | 'datetime' = 'date'): string {
    const currentLang = this._translate.currentLang;
    if (currentLang !== this._cachedLang) {
      const months = APP_DATE_MONTH_KEYS.map((key) => this._translate.instant(key));
      // Guard: instant() returns the key string when translations are not yet
      // loaded.  Only write to cache when every value differs from the key it
      // came from.  If the cache were written with key-strings as month values
      // it would be permanently poisoned — currentLang never changes again to
      // invalidate it.
      const resolved = months.every((v, i) => v !== APP_DATE_MONTH_KEYS[i]);
      if (resolved) {
        this._cachedLang = currentLang;
        this._cachedMonths = months;
      } else {
        // Translations not yet available — format without caching so the next
        // call retries once the loader has finished.
        return formatAppDate(value, mode, months);
      }
    }
    return formatAppDate(value, mode, this._cachedMonths);
  }
}
